import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  Fingerprint, Wifi, WifiOff, RefreshCw, Plus, Settings,
  Clock, RotateCcw, Trash2, UserPlus, UserMinus, Terminal,
  AlertCircle, CheckCircle, Activity, Server, History, Eye, Edit,
  Bell, Mail, X, PlusCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function ZKTecoDevices() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('devices');
  const [devices, setDevices] = useState([]);
  const [unregisteredDevices, setUnregisteredDevices] = useState([]);
  const [attendanceLogs, setAttendanceLogs] = useState([]);
  const [rawLogs, setRawLogs] = useState([]);
  const [commands, setCommands] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [syncAuditLogs, setSyncAuditLogs] = useState([]);
  const [syncAuditFilter, setSyncAuditFilter] = useState('all'); // all | sync | unsync | error
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Modals
  const [registerModalOpen, setRegisterModalOpen] = useState(false);
  const [commandModalOpen, setCommandModalOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedUnregistered, setSelectedUnregistered] = useState(null);
  const [timezoneModalOpen, setTimezoneModalOpen] = useState(false);
  const [timezoneDraft, setTimezoneDraft] = useState('Africa/Kinshasa');
  const [editDeviceModalOpen, setEditDeviceModalOpen] = useState(false);
  const [editDeviceForm, setEditDeviceForm] = useState({
    device_name: '',
    location: '',
    allowed_company_ids: [],
  });
  // Alertes hors-ligne
  const [alertModalOpen, setAlertModalOpen] = useState(false);
  const [alertForm, setAlertForm] = useState({
    recipients: [],
    newEmail: '',
    threshold: 15,
  });
  const [alertSaving, setAlertSaving] = useState(false);
  
  // Filters
  const [logDeviceFilter, setLogDeviceFilter] = useState('all');
  const [logDateFilter, setLogDateFilter] = useState(new Date().toISOString().split('T')[0]);
  
  // Form data
  const [registerForm, setRegisterForm] = useState({
    serial_number: '',
    device_name: '',
    location: '',
    company_id: '',
    allowed_company_ids: []
  });
  
  const [commandForm, setCommandForm] = useState({
    command_type: 'sync_time',
    pin: '',
    name: '',
    employee_id: ''
  });

  // Unsynced employees for the selected device (used by 'add_user' combobox)
  const [unsyncedEmployees, setUnsyncedEmployees] = useState([]);
  const [unsyncedLoading, setUnsyncedLoading] = useState(false);

  // Bulk-sync progress tracking
  const [syncProgress, setSyncProgress] = useState({
    running: false,
    total: 0,
    completed: 0,
    current: null,
    failed: []
  });
  const [showSyncResults, setShowSyncResults] = useState(false);

  const canManage = ['super_admin', 'company_admin'].includes(user?.role);
  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    fetchAllData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchDeviceStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (activeTab === 'logs') {
      fetchAttendanceLogs();
    } else if (activeTab === 'debug') {
      fetchRawLogs();
      fetchCommands();
    } else if (activeTab === 'sync-audit') {
      fetchSyncAuditLogs();
    }
  }, [activeTab, logDeviceFilter, logDateFilter, syncAuditFilter]);

  // Load unsynced employees when the admin opens 'add_user' command for a device
  useEffect(() => {
    if (commandModalOpen && commandForm.command_type === 'add_user' && selectedDevice) {
      fetchUnsyncedEmployees();
    }
  }, [commandModalOpen, commandForm.command_type, selectedDevice]);

  const fetchUnsyncedEmployees = async () => {
    if (!selectedDevice) return;
    const serial = selectedDevice.serial_number || selectedDevice.device_id;
    if (!serial) return;
    setUnsyncedLoading(true);
    try {
      const response = await api.get(`/employees/device/${serial}/unsynced-employees`);
      setUnsyncedEmployees(response.data?.employees || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur chargement employés');
      setUnsyncedEmployees([]);
    } finally {
      setUnsyncedLoading(false);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    const promises = [
      fetchDeviceStatus(),
      fetchUnregisteredDevices()
    ];
    // Fetch companies only for super_admin
    if (user?.role === 'super_admin') {
      promises.push(fetchCompanies());
    }
    await Promise.all(promises);
    setLoading(false);
  };

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(response.data);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  };

  const fetchDeviceStatus = async () => {
    try {
      const response = await api.get('/zkteco/devices/status');
      setDevices(response.data);
    } catch (error) {
      console.error('Failed to fetch devices:', error);
    }
  };

  const fetchUnregisteredDevices = async () => {
    try {
      const response = await api.get('/zkteco/unregistered-devices');
      setUnregisteredDevices(response.data);
    } catch (error) {
      console.error('Failed to fetch unregistered devices:', error);
    }
  };

  const fetchAttendanceLogs = async () => {
    try {
      const params = new URLSearchParams();
      if (logDeviceFilter && logDeviceFilter !== 'all') {
        params.append('device_serial', logDeviceFilter);
      }
      if (logDateFilter) {
        params.append('date', logDateFilter);
      }
      params.append('limit', '100');
      
      const response = await api.get(`/zkteco/attendance-logs?${params.toString()}`);
      setAttendanceLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch attendance logs:', error);
    }
  };

  const fetchRawLogs = async () => {
    try {
      const response = await api.get('/zkteco/raw-logs?limit=50');
      setRawLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch raw logs:', error);
    }
  };

  const fetchCommands = async () => {
    try {
      const response = await api.get('/zkteco/commands?limit=50');
      setCommands(response.data);
    } catch (error) {
      console.error('Failed to fetch commands:', error);
    }
  };

  const fetchSyncAuditLogs = async () => {
    try {
      const params = { limit: 100 };
      if (syncAuditFilter === 'sync' || syncAuditFilter === 'unsync') {
        params.action = syncAuditFilter;
      } else if (syncAuditFilter === 'error') {
        params.status = 'error';
      }
      const response = await api.get('/zkteco/sync-audit-logs', { params });
      setSyncAuditLogs(response.data?.logs || []);
    } catch (error) {
      console.error('Failed to fetch sync audit logs:', error);
      setSyncAuditLogs([]);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    if (activeTab === 'logs') await fetchAttendanceLogs();
    if (activeTab === 'debug') {
      await fetchRawLogs();
      await fetchCommands();
    }
    if (activeTab === 'sync-audit') await fetchSyncAuditLogs();
    setRefreshing(false);
    toast.success('Données actualisées');
  };

  const handleRegisterDevice = async (e) => {
    e.preventDefault();
    try {
      await api.post('/zkteco/devices/register', null, {
        params: registerForm,
        paramsSerializer: { indexes: null },
      });
      toast.success('Appareil enregistré avec succès');
      setRegisterModalOpen(false);
      setRegisterForm({ serial_number: '', device_name: '', location: '', company_id: '', allowed_company_ids: [] });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleSendCommand = async (e) => {
    e.preventDefault();
    if (!selectedDevice) return;

    const online = isDeviceReallyOnline(selectedDevice);
    const offlineNote = online
      ? ''
      : ' (pointeuse hors ligne — appliqué à la prochaine communication)';

    try {
      if (commandForm.command_type === 'add_user') {
        // Validate employee selection
        if (!commandForm.employee_id) {
          toast.error("Veuillez sélectionner un employé");
          return;
        }
        // Use the sync endpoint: queues add_user command AND records sync state
        const actualSerial = selectedDevice.serial_number || selectedDevice.device_id;
        await api.post(`/employees/${commandForm.employee_id}/sync`, {
          device_ids: [actualSerial]
        });
        if (online) {
          toast.success(`Utilisateur ${commandForm.name} envoyé à la pointeuse`);
        } else {
          toast.warning(
            `Utilisateur ${commandForm.name} mis en file d'attente${offlineNote}`,
            { duration: 6000 }
          );
        }
        setCommandModalOpen(false);
        setCommandForm({ command_type: 'sync_time', pin: '', name: '', employee_id: '' });
        fetchCommands();
        return;
      }

      const params = { command_type: commandForm.command_type };
      if (commandForm.command_type === 'delete_user') {
        params.pin = commandForm.pin;
      }

      await api.post(`/zkteco/devices/${selectedDevice.serial_number || selectedDevice.device_id}/command`, null, { params });
      if (online) {
        toast.success(`Commande "${commandForm.command_type}" envoyée`);
      } else {
        toast.warning(
          `Commande "${commandForm.command_type}" mise en file${offlineNote}`,
          { duration: 6000 }
        );
      }
      setCommandModalOpen(false);
      setCommandForm({ command_type: 'sync_time', pin: '', name: '', employee_id: '' });
      fetchCommands();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi de la commande');
    }
  };

  const handleSyncAllUnsynced = async (targetEmployees = null) => {
    if (!selectedDevice) return;
    const list = targetEmployees || unsyncedEmployees;
    if (list.length === 0) {
      toast.info("Aucun employé à synchroniser");
      return;
    }
    const actualSerial = selectedDevice.serial_number || selectedDevice.device_id;

    setSyncProgress({
      running: true,
      total: list.length,
      completed: 0,
      current: list[0]?.full_name || null,
      failed: []
    });

    const failed = [];
    for (let i = 0; i < list.length; i++) {
      const emp = list[i];
      setSyncProgress(prev => ({ ...prev, current: emp.full_name, completed: i }));
      try {
        await api.post(`/employees/${emp.employee_id}/sync`, {
          device_ids: [actualSerial]
        });
      } catch (error) {
        failed.push({
          employee_id: emp.employee_id,
          full_name: emp.full_name,
          pin: emp.pin,
          error: error.response?.data?.detail || error.message || 'Erreur inconnue'
        });
      }
    }

    setSyncProgress(prev => ({
      ...prev,
      running: false,
      completed: list.length,
      current: null,
      failed
    }));

    const successCount = list.length - failed.length;
    if (failed.length === 0) {
      toast.success(`${successCount} employé(s) envoyé(s) à la pointeuse`);
      setCommandModalOpen(false);
      setCommandForm({ command_type: 'sync_time', pin: '', name: '', employee_id: '' });
      setSyncProgress({ running: false, total: 0, completed: 0, current: null, failed: [] });
    } else {
      toast.warning(`${successCount} réussi(s), ${failed.length} en échec`);
      setShowSyncResults(true);
    }
    fetchCommands();
  };

  const handleRetryFailed = async () => {
    const toRetry = syncProgress.failed.map(f => ({
      employee_id: f.employee_id,
      full_name: f.full_name,
      pin: f.pin
    }));
    setShowSyncResults(false);
    await handleSyncAllUnsynced(toRetry);
  };

  const handleQuickSyncTime = async (device) => {
    const serial = device.serial_number || device.device_id;
    const name = device.device_name || serial;
    const online = isDeviceReallyOnline(device);
    try {
      await api.post(`/zkteco/devices/${serial}/command`, null, {
        params: { command_type: 'sync_time' }
      });
      if (online) {
        toast.success(`Synchronisation d'heure envoyée à ${name}.`);
      } else {
        toast.warning(
          `Synchronisation mise en file. La pointeuse ${name} est hors ligne — la commande sera appliquée à sa prochaine communication.`,
          { duration: 6000 }
        );
      }
      fetchCommands();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi de la commande");
    }
  };

  const handleForceReconnect = async (device) => {
    const serial = device.serial_number || device.device_id;
    const name = device.device_name || serial;
    if (!window.confirm(
      `Forcer la reconnexion de "${name}" ?\n\n` +
      `Cela va :\n` +
      `• Annuler toutes les commandes en attente (REBOOT, sync_time, etc.)\n` +
      `• Marquer la pointeuse comme active\n\n` +
      `Note : la pointeuse ne redeviendra "En ligne" que lorsqu'elle communiquera à nouveau réellement avec le serveur.`
    )) return;
    try {
      const res = await api.post(`/zkteco/devices/${serial}/force-reconnect`);
      toast.success(
        `File de commandes purgée pour ${name}. ` +
        `${res.data.cancelled_commands || 0} commande(s) annulée(s).`
      );
      fetchDeviceStatus();
      fetchCommands();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    }
  };

  const handleFixDrift = async (device) => {
    const serial = device.serial_number || device.device_id;
    const name = device.device_name || serial;
    const drift = device.last_drift_seconds;
    const online = isDeviceReallyOnline(device);
    const driftLabel = drift !== null && drift !== undefined
      ? `${drift > 0 ? '+' : ''}${Math.round(drift / 60)}min`
      : 'inconnu';

    if (!online && !window.confirm(
      `La pointeuse "${name}" est actuellement HORS LIGNE.\n\n` +
      `Écart actuel : ${driftLabel}\n\n` +
      `La commande de correction sera mise en file d'attente. ` +
      `Elle sera appliquée dès que la pointeuse se reconnectera.\n\nContinuer ?`
    )) return;

    try {
      await api.post(`/zkteco/devices/${serial}/command`, null, {
        params: { command_type: 'sync_time' }
      });
      toast.success(
        online
          ? `Correction envoyée à ${name}.`
          : `Correction mise en file. Sera appliquée à la reconnexion de ${name}.`
      );
      fetchCommands();
      fetchDeviceStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la correction');
    }
  };

  // A device is "really online" only if it hit the server within the last 5 min.
  const isDeviceReallyOnline = (device) => {
    if (!device.last_seen) return false;
    try {
      const ts = new Date(device.last_seen).getTime();
      return (Date.now() - ts) < 5 * 60 * 1000;
    } catch { return false; }
  };

  const openEditDevice = (device) => {
    setSelectedDevice(device);
    setEditDeviceForm({
      device_name: device.device_name || '',
      location: device.location || '',
      allowed_company_ids: device.allowed_company_ids || [],
    });
    setEditDeviceModalOpen(true);
  };

  const openAlertModal = (device) => {
    setSelectedDevice(device);
    setAlertForm({
      recipients: device.alert_recipients || [],
      newEmail: '',
      threshold: device.offline_threshold_minutes || 15,
    });
    setAlertModalOpen(true);
  };

  const handleSaveAlertRecipients = async () => {
    if (!selectedDevice) return;
    setAlertSaving(true);
    const serial = selectedDevice.serial_number || selectedDevice.device_id;
    try {
      await api.patch(`/zkteco/devices/${serial}/alert-recipients`, {
        alert_recipients: alertForm.recipients,
        offline_threshold_minutes: alertForm.threshold,
      });
      toast.success('Destinataires d\'alertes mis à jour');
      setAlertModalOpen(false);
      fetchDeviceStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setAlertSaving(false);
    }
  };

  const addAlertEmail = () => {
    const email = alertForm.newEmail.trim().toLowerCase();
    if (!email) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Adresse email invalide');
      return;
    }
    if (alertForm.recipients.includes(email)) {
      toast.error('Cet email est déjà dans la liste');
      return;
    }
    setAlertForm(prev => ({ ...prev, recipients: [...prev.recipients, email], newEmail: '' }));
  };

  const removeAlertEmail = (email) => {
    setAlertForm(prev => ({ ...prev, recipients: prev.recipients.filter(e => e !== email) }));
  };

  const handleSaveEditDevice = async (e) => {
    e.preventDefault();
    if (!selectedDevice) return;
    const serial = selectedDevice.serial_number || selectedDevice.device_id;
    try {
      await api.patch(`/zkteco/devices/${serial}`, {
        device_name: editDeviceForm.device_name,
        location: editDeviceForm.location,
        allowed_company_ids: editDeviceForm.allowed_company_ids,
      });
      toast.success('Pointeuse mise à jour');
      setEditDeviceModalOpen(false);
      fetchDeviceStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const renderDriftBadge = (device) => {
    const drift = device.last_drift_seconds;
    const measuredAt = device.last_drift_measured_at;
    const autoSyncAt = device.last_auto_sync_time_at;
    const deviceTime = device.last_device_time; // heure directe de la pointeuse

    if (drift === undefined || drift === null) {
      return <span className="text-xs text-slate-400" data-testid={`drift-${device.serial_number}`}>—</span>;
    }

    const abs = Math.abs(drift);
    let color = 'bg-green-100 text-green-800 border-green-300';
    if (abs < 60) {
      color = 'bg-green-100 text-green-800 border-green-300';
    } else if (abs < 120) {
      color = 'bg-amber-100 text-amber-800 border-amber-300';
    } else {
      color = 'bg-red-100 text-red-800 border-red-300';
    }

    let label;
    if (abs >= 60) {
      const mins = Math.floor(abs / 60);
      const secs = abs % 60;
      label = `${drift > 0 ? '+' : '-'}${mins}min${secs ? ` ${secs}s` : ''}`;
    } else {
      label = `${drift >= 0 ? '+' : ''}${drift}s`;
    }

    const isRecent = autoSyncAt && measuredAt
      && new Date(autoSyncAt).getTime() >= new Date(measuredAt).getTime() - 1000;

    const needsFix = abs >= 60;

    // Tooltip : affiche l'heure de la pointeuse si disponible
    const tooltipParts = [];
    if (deviceTime) tooltipParts.push(`⏱ Heure pointeuse : ${deviceTime}`);
    if (measuredAt) {
      const ago = Math.round((Date.now() - new Date(measuredAt).getTime()) / 1000);
      tooltipParts.push(`Mesuré il y a ${ago < 60 ? ago + 's' : Math.floor(ago/60) + 'min'}`);
    }

    return (
      <div className="flex flex-col gap-1" data-testid={`drift-${device.serial_number}`}>
        <div className="flex items-center gap-1 flex-wrap">
          <Badge
            variant="outline"
            className={`${color} font-mono cursor-help`}
            title={tooltipParts.join(' | ')}
          >
            {label}
          </Badge>
          {/* Indicateur source de mesure */}
          {deviceTime && (
            <span className="text-xs text-slate-500" title={`Heure pointeuse : ${deviceTime}`}>
              🕐
            </span>
          )}
          {needsFix && (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 px-2 text-xs"
              onClick={() => handleFixDrift(device)}
              title="Corriger l'écart horloge (envoie une commande de synchronisation à la pointeuse)"
              data-testid={`fix-drift-${device.serial_number}`}
            >
              <RotateCcw className="w-3 h-3 mr-1 text-blue-600" />
              Corriger
            </Button>
          )}
        </div>
        {/* Heure réelle de la pointeuse */}
        {deviceTime && (
          <span className="text-xs text-slate-500 font-mono" title="Heure actuelle de la pointeuse">
            ⏱ {deviceTime}
          </span>
        )}
        {isRecent && (
          <span className="text-xs text-blue-600 flex items-center gap-1" data-testid={`drift-autosync-${device.serial_number}`}>
            <RefreshCw className="w-3 h-3" />
            Sync auto envoyée
          </span>
        )}
      </div>
    );

  };

  const openTimezoneEditor = (device) => {    setSelectedDevice(device);
    setTimezoneDraft(device.timezone || 'Africa/Kinshasa');
    setTimezoneModalOpen(true);
  };

  const handleSaveTimezone = async (e) => {
    e.preventDefault();
    if (!selectedDevice) return;
    const serial = selectedDevice.serial_number || selectedDevice.device_id;
    try {
      await api.patch(`/zkteco/devices/${serial}/timezone`, null, {
        params: { timezone_name: timezoneDraft }
      });
      toast.success(`Fuseau horaire mis à jour: ${timezoneDraft}`);
      setTimezoneModalOpen(false);
      fetchDeviceStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const openRegisterFromUnregistered = (device) => {    setSelectedUnregistered(device);
    setRegisterForm({
      serial_number: device.serial_number,
      device_name: `Pointeuse ${device.serial_number}`,
      location: '',
      company_id: user?.company_id || ''
    });
    setRegisterModalOpen(true);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return format(date, 'dd/MM/yyyy HH:mm:ss', { locale: fr });
    } catch {
      return dateStr;
    }
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return formatDistanceToNow(date, { addSuffix: true, locale: fr });
    } catch {
      return dateStr;
    }
  };

  const getPunchTypeLabel = (type) => {
    const labels = {
      'check_in': 'Entrée',
      'check_out': 'Sortie',
      'break_out': 'Pause sortie',
      'break_in': 'Pause retour',
      'overtime_in': 'HS début',
      'overtime_out': 'HS fin'
    };
    return labels[type] || type;
  };

  const getPunchTypeBadge = (type) => {
    const colors = {
      'check_in': 'bg-green-100 text-green-800',
      'check_out': 'bg-blue-100 text-blue-800',
      'break_out': 'bg-amber-100 text-amber-800',
      'break_in': 'bg-amber-100 text-amber-800',
      'overtime_in': 'bg-purple-100 text-purple-800',
      'overtime_out': 'bg-purple-100 text-purple-800'
    };
    return colors[type] || 'bg-slate-100 text-slate-800';
  };

  const onlineCount = devices.filter(d => d.is_online).length;
  const offlineCount = devices.filter(d => !d.is_online).length;

  const openNewDeviceModal = () => {
    setSelectedUnregistered(null);
    setRegisterForm({
      serial_number: '',
      device_name: '',
      location: '',
      company_id: user?.company_id || ''
    });
    setRegisterModalOpen(true);
  };

  return (
    <div className="p-6 space-y-6" data-testid="zkteco-devices-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Fingerprint className="w-6 h-6" />
            Gestion des Pointeuses ZKTeco
          </h1>
          <p className="text-slate-600 mt-1">
            Surveillez et gérez vos appareils biométriques MB160 Plus
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
          {canManage && (
            <Button onClick={openNewDeviceModal}>
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un pointeur
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-full">
                <Server className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total appareils</p>
                <p className="text-2xl font-bold">{devices.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-full">
                <Wifi className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">En ligne</p>
                <p className="text-2xl font-bold text-green-600">{onlineCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-100 rounded-full">
                <WifiOff className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Hors ligne</p>
                <p className="text-2xl font-bold text-red-600">{offlineCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-full">
                <AlertCircle className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Non enregistrés</p>
                <p className="text-2xl font-bold text-amber-600">{unregisteredDevices.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="devices" data-testid="tab-devices">
            <Server className="w-4 h-4 mr-2" />
            Appareils ({devices.length})
          </TabsTrigger>
          <TabsTrigger value="unregistered" data-testid="tab-unregistered">
            <AlertCircle className="w-4 h-4 mr-2" />
            Non enregistrés ({unregisteredDevices.length})
          </TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs">
            <Activity className="w-4 h-4 mr-2" />
            Pointages
          </TabsTrigger>
          <TabsTrigger value="debug" data-testid="tab-debug">
            <Terminal className="w-4 h-4 mr-2" />
            Debug
          </TabsTrigger>
          <TabsTrigger value="sync-audit" data-testid="tab-sync-audit">
            <History className="w-4 h-4 mr-2" />
            Journal de sync
          </TabsTrigger>
        </TabsList>

        {/* Registered Devices Tab */}
        <TabsContent value="devices" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Appareils enregistrés</CardTitle>
              <CardDescription>Liste des pointeuses ZKTeco configurées</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-slate-500">Chargement...</div>
              ) : devices.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Server className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>Aucun appareil enregistré</p>
                  <p className="text-sm mt-1">Les appareils apparaîtront ici une fois configurés</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Statut</TableHead>
                      <TableHead>Nom</TableHead>
                      <TableHead>N° Série</TableHead>
                      {isSuperAdmin && <TableHead>Entreprise</TableHead>}
                      <TableHead>Emplacement</TableHead>
                      <TableHead>Fuseau horaire</TableHead>
                      <TableHead>Écart horloge</TableHead>
                      <TableHead>Dernière connexion</TableHead>
                      <TableHead>IP</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {devices.map((device) => (
                      <TableRow key={device.id || device.serial_number}>
                        <TableCell>
                          {device.is_online ? (
                            <Badge className="bg-green-100 text-green-800">
                              <Wifi className="w-3 h-3 mr-1" />
                              En ligne
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800">
                              <WifiOff className="w-3 h-3 mr-1" />
                              Hors ligne
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{device.device_name}</TableCell>
                        <TableCell className="font-mono text-sm">{device.serial_number || device.device_id}</TableCell>
                        {isSuperAdmin && (
                          <TableCell className="text-sm">
                            {companies.find(c => c.id === device.company_id)?.name || device.company_id || '-'}
                          </TableCell>
                        )}
                        <TableCell>{device.location || '-'}</TableCell>
                        <TableCell className="text-sm">
                          <div className="flex items-center gap-2" data-testid={`device-tz-${device.serial_number || device.device_id}`}>
                            <span className="font-mono">{device.timezone || 'Africa/Kinshasa'}</span>
                            {isSuperAdmin && (
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-6 w-6"
                                onClick={() => openTimezoneEditor(device)}
                                title="Modifier le fuseau horaire"
                                data-testid={`edit-tz-${device.serial_number || device.device_id}`}
                              >
                                <Edit className="w-3 h-3 text-blue-500" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {renderDriftBadge(device)}
                        </TableCell>
                        <TableCell>
                          <span title={formatDateTime(device.last_seen)}>
                            {formatTimeAgo(device.last_seen)}
                          </span>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{device.last_ip || '-'}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleForceReconnect(device)}
                            title="Forcer la reconnexion : vide la file de commandes et remet la pointeuse à l'état actif"
                            data-testid={`force-reconnect-${device.serial_number || device.device_id}`}
                          >
                            <Wifi className="w-4 h-4 text-emerald-600" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleQuickSyncTime(device)}
                            title="Synchroniser l'heure de la pointeuse maintenant"
                            data-testid={`sync-time-now-${device.serial_number || device.device_id}`}
                          >
                            <Clock className="w-4 h-4 text-blue-600" />
                          </Button>
                          {canManage && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditDevice(device)}
                              title="Modifier le nom, l'emplacement et les entreprises additionnelles"
                              data-testid={`edit-device-${device.serial_number || device.device_id}`}
                            >
                              <Edit className="w-4 h-4 text-amber-600" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openAlertModal(device)}
                            title="Configurer les alertes hors-ligne"
                            data-testid={`alert-device-${device.serial_number || device.device_id}`}
                            className="relative"
                          >
                            <Bell className={`w-4 h-4 ${device.alert_recipients?.length > 0 ? 'text-orange-500' : 'text-slate-400'}`} />
                            {device.alert_recipients?.length > 0 && (
                              <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-[9px] rounded-full w-3.5 h-3.5 flex items-center justify-center font-bold">
                                {device.alert_recipients.length}
                              </span>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedDevice(device);
                              setCommandModalOpen(true);
                            }}
                            title="Envoyer une commande"
                            data-testid={`cmd-device-${device.serial_number || device.device_id}`}
                          >
                            <Settings className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Unregistered Devices Tab */}
        <TabsContent value="unregistered" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Appareils non enregistrés</CardTitle>
              <CardDescription>
                Appareils qui ont contacté le serveur mais ne sont pas encore configurés
              </CardDescription>
            </CardHeader>
            <CardContent>
              {unregisteredDevices.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-300" />
                  <p>Tous les appareils sont enregistrés</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>N° Série</TableHead>
                      <TableHead>Version Push</TableHead>
                      <TableHead>Première connexion</TableHead>
                      <TableHead>Dernière connexion</TableHead>
                      <TableHead>IP</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {unregisteredDevices.map((device) => (
                      <TableRow key={device.serial_number}>
                        <TableCell className="font-mono font-medium">{device.serial_number}</TableCell>
                        <TableCell>{device.push_version || '-'}</TableCell>
                        <TableCell>{formatDateTime(device.first_seen)}</TableCell>
                        <TableCell>{formatTimeAgo(device.last_seen)}</TableCell>
                        <TableCell className="font-mono text-sm">{device.ip_address || '-'}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            onClick={() => openRegisterFromUnregistered(device)}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Enregistrer
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Attendance Logs Tab */}
        <TabsContent value="logs" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Logs de Pointage</CardTitle>
                  <CardDescription>Pointages reçus des appareils ZKTeco</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Select value={logDeviceFilter} onValueChange={setLogDeviceFilter}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Tous les appareils" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les appareils</SelectItem>
                      {devices.map(d => (
                        <SelectItem key={d.serial_number} value={d.serial_number}>
                          {d.device_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="date"
                    value={logDateFilter}
                    onChange={(e) => setLogDateFilter(e.target.value)}
                    className="w-[150px]"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {attendanceLogs.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Activity className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>Aucun pointage pour cette période</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date/Heure</TableHead>
                      <TableHead>ID Biométrique</TableHead>
                      <TableHead>Employé</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Appareil</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {attendanceLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="font-mono text-sm">
                          {formatDateTime(log.timestamp)}
                        </TableCell>
                        <TableCell className="font-mono">{log.biometric_user_id}</TableCell>
                        <TableCell>
                          {log.employee_name || (
                            <span className="text-slate-400 italic">Non associé</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={getPunchTypeBadge(log.punch_type_name)}>
                            {getPunchTypeLabel(log.punch_type_name)}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{log.device_serial}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Debug Tab */}
        <TabsContent value="debug" className="mt-4 space-y-4">
          {/* Commands History */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Terminal className="w-5 h-5" />
                Historique des Commandes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {commands.length === 0 ? (
                <div className="text-center py-4 text-slate-500">Aucune commande envoyée</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Appareil</TableHead>
                      <TableHead>Commande</TableHead>
                      <TableHead>Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {commands.slice(0, 20).map((cmd) => (
                      <TableRow key={cmd.id}>
                        <TableCell className="text-sm">{formatDateTime(cmd.created_at)}</TableCell>
                        <TableCell className="font-mono text-sm">{cmd.device_serial}</TableCell>
                        <TableCell className="font-mono text-xs max-w-[300px] truncate">
                          {cmd.command}
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            cmd.status === 'completed' ? 'bg-green-100 text-green-800' :
                            cmd.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                            'bg-amber-100 text-amber-800'
                          }>
                            {cmd.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Raw Logs */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Logs Bruts (Debug)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {rawLogs.length === 0 ? (
                <div className="text-center py-4 text-slate-500">Aucun log brut</div>
              ) : (
                <div className="max-h-[400px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[150px]">Date</TableHead>
                        <TableHead className="w-[120px]">Type</TableHead>
                        <TableHead className="w-[120px]">Appareil</TableHead>
                        <TableHead>Données</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rawLogs.slice(0, 30).map((log) => (
                        <TableRow key={log.id}>
                          <TableCell className="text-xs">{formatDateTime(log.timestamp)}</TableCell>
                          <TableCell className="text-xs">{log.request_type}</TableCell>
                          <TableCell className="font-mono text-xs">{log.serial_number}</TableCell>
                          <TableCell>
                            <pre className="text-xs bg-slate-50 p-1 rounded max-w-[400px] overflow-x-auto">
                              {log.data?.substring(0, 200)}
                              {log.data?.length > 200 && '...'}
                            </pre>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sync Audit Tab */}
        <TabsContent value="sync-audit" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <History className="w-5 h-5" />
                    Journal d'audit des synchronisations
                  </CardTitle>
                  <CardDescription>
                    Historique des envois et suppressions d'employés sur les pointeuses
                  </CardDescription>
                </div>
                <div className="w-56">
                  <Select value={syncAuditFilter} onValueChange={setSyncAuditFilter}>
                    <SelectTrigger data-testid="sync-audit-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les événements</SelectItem>
                      <SelectItem value="sync">Synchronisations uniquement</SelectItem>
                      <SelectItem value="unsync">Suppressions uniquement</SelectItem>
                      <SelectItem value="error">Échecs uniquement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {syncAuditLogs.length === 0 ? (
                <div className="text-center py-10 text-slate-500" data-testid="sync-audit-empty">
                  Aucun événement de synchronisation enregistré
                </div>
              ) : (
                <div className="max-h-[600px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[160px]">Date</TableHead>
                        <TableHead>Employé</TableHead>
                        <TableHead className="w-[100px]">PIN</TableHead>
                        <TableHead>Pointeuse</TableHead>
                        <TableHead className="w-[110px]">Action</TableHead>
                        <TableHead className="w-[110px]">Statut</TableHead>
                        <TableHead>Effectué par</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {syncAuditLogs.map((log) => (
                        <TableRow key={log.id} data-testid={`sync-audit-row-${log.id}`}>
                          <TableCell className="text-xs text-slate-600">
                            {formatDateTime(log.created_at)}
                          </TableCell>
                          <TableCell className="font-medium text-sm">{log.employee_name}</TableCell>
                          <TableCell className="font-mono text-xs">{log.pin}</TableCell>
                          <TableCell className="text-sm">
                            <div className="font-medium">{log.device_name || '—'}</div>
                            <div className="font-mono text-xs text-slate-500">{log.device_serial}</div>
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              log.action === 'sync'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-orange-100 text-orange-800'
                            }>
                              {log.action === 'sync' ? (
                                <><UserPlus className="w-3 h-3 mr-1" />Envoi</>
                              ) : (
                                <><UserMinus className="w-3 h-3 mr-1" />Suppression</>
                              )}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {log.status === 'success' ? (
                              <Badge className="bg-green-100 text-green-800">
                                <CheckCircle className="w-3 h-3 mr-1" />Succès
                              </Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-800" title={log.error_message || ''}>
                                <AlertCircle className="w-3 h-3 mr-1" />Échec
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-slate-600">
                            {log.performed_by_name || log.performed_by?.substring(0, 8) || '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Register Device Modal */}
      <Dialog open={registerModalOpen} onOpenChange={setRegisterModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Enregistrer un appareil</DialogTitle>
            <DialogDescription>
              Configurez les informations de cet appareil ZKTeco
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleRegisterDevice} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="serial_number">Numéro de série *</Label>
              <Input
                id="serial_number"
                value={registerForm.serial_number}
                onChange={(e) => setRegisterForm({ ...registerForm, serial_number: e.target.value })}
                placeholder="MB160_001"
                required
                readOnly={!!selectedUnregistered}
                className={selectedUnregistered ? 'bg-slate-50' : ''}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="device_name">Nom de l'appareil *</Label>
              <Input
                id="device_name"
                value={registerForm.device_name}
                onChange={(e) => setRegisterForm({ ...registerForm, device_name: e.target.value })}
                placeholder="Pointeuse Entrée Principale"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="location">Emplacement *</Label>
              <Input
                id="location"
                value={registerForm.location}
                onChange={(e) => setRegisterForm({ ...registerForm, location: e.target.value })}
                placeholder="Hall d'entrée, Bâtiment A"
                required
              />
            </div>

            {/* Company selector - visible for all users */}
            <div className="space-y-2">
              <Label htmlFor="company_id">Entreprise *</Label>
              {isSuperAdmin ? (
                <Select
                  value={registerForm.company_id}
                  onValueChange={(value) => setRegisterForm({ ...registerForm, company_id: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner une entreprise" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map(company => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="company_id"
                  value={companies.find(c => c.id === registerForm.company_id)?.name || 'Votre entreprise'}
                  readOnly
                  className="bg-slate-50"
                />
              )}
            </div>

            {companies.length > 1 && (
              <div className="space-y-2">
                <Label>Entreprises additionnelles (optionnel)</Label>
                <p className="text-xs text-slate-500">
                  Cochez d&apos;autres entreprises dont les employés peuvent pointer sur cette pointeuse (multi-tenant).
                </p>
                <div className="border rounded-md p-2 max-h-40 overflow-y-auto space-y-1 bg-slate-50" data-testid="device-allowed-companies">
                  {companies.filter(c => c.id !== registerForm.company_id).map((c) => {
                    const checked = (registerForm.allowed_company_ids || []).includes(c.id);
                    return (
                      <label key={c.id} className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded text-sm">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const current = registerForm.allowed_company_ids || [];
                            setRegisterForm({
                              ...registerForm,
                              allowed_company_ids: e.target.checked
                                ? [...current, c.id]
                                : current.filter((x) => x !== c.id),
                            });
                          }}
                          data-testid={`device-allow-${c.id}`}
                        />
                        <span>{c.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            
            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setRegisterModalOpen(false)} className="flex-1">
                Annuler
              </Button>
              <Button type="submit" className="flex-1" disabled={isSuperAdmin && !registerForm.company_id}>
                <Plus className="w-4 h-4 mr-2" />
                Enregistrer
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Command Modal */}
      <Dialog open={commandModalOpen} onOpenChange={setCommandModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Envoyer une commande</DialogTitle>
            <DialogDescription>
              {selectedDevice && `Appareil: ${selectedDevice.device_name} (${selectedDevice.serial_number || selectedDevice.device_id})`}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSendCommand} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="command_type">Type de commande</Label>
              <Select
                value={commandForm.command_type}
                onValueChange={(value) => setCommandForm({ ...commandForm, command_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sync_time">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Synchroniser l'heure
                    </div>
                  </SelectItem>
                  <SelectItem value="reboot">
                    <div className="flex items-center gap-2">
                      <RotateCcw className="w-4 h-4" />
                      Redémarrer l'appareil
                    </div>
                  </SelectItem>
                  <SelectItem value="clear_log">
                    <div className="flex items-center gap-2">
                      <Trash2 className="w-4 h-4" />
                      Effacer les logs
                    </div>
                  </SelectItem>
                  <SelectItem value="add_user">
                    <div className="flex items-center gap-2">
                      <UserPlus className="w-4 h-4" />
                      Ajouter un utilisateur
                    </div>
                  </SelectItem>
                  <SelectItem value="delete_user">
                    <div className="flex items-center gap-2">
                      <UserMinus className="w-4 h-4" />
                      Supprimer un utilisateur
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {commandForm.command_type === 'delete_user' && (
              <div className="space-y-2">
                <Label htmlFor="pin">PIN / ID Biométrique *</Label>
                <Input
                  id="pin"
                  value={commandForm.pin}
                  onChange={(e) => setCommandForm({ ...commandForm, pin: e.target.value })}
                  placeholder="12345"
                  required
                  data-testid="delete-user-pin-input"
                />
              </div>
            )}

            {commandForm.command_type === 'add_user' && (
              <div className="space-y-2">
                <Label htmlFor="employee-combobox">Sélectionner un employé *</Label>
                <Select
                  value={commandForm.employee_id}
                  onValueChange={(value) => {
                    const emp = unsyncedEmployees.find(e => e.employee_id === value);
                    setCommandForm({
                      ...commandForm,
                      employee_id: value,
                      pin: emp?.pin || '',
                      name: emp?.full_name || ''
                    });
                  }}
                >
                  <SelectTrigger id="employee-combobox" data-testid="add-user-employee-select">
                    <SelectValue placeholder={unsyncedLoading ? 'Chargement...' : 'Choisir un employé'} />
                  </SelectTrigger>
                  <SelectContent>
                    {unsyncedEmployees.length === 0 && !unsyncedLoading && (
                      <div className="px-3 py-2 text-sm text-slate-500">
                        Aucun employé disponible (tous déjà synchronisés ou sans ID biométrique)
                      </div>
                    )}
                    {unsyncedEmployees.map(emp => (
                      <SelectItem
                        key={emp.employee_id}
                        value={emp.employee_id}
                        data-testid={`unsynced-employee-${emp.employee_id}`}
                      >
                        <div className="flex items-center justify-between gap-3 w-full">
                          <span className="font-medium">{emp.full_name}</span>
                          <span className="text-xs text-slate-500">PIN {emp.pin}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {commandForm.employee_id && (
                  <p className="text-xs text-slate-500">
                    PIN envoyé à la pointeuse : <span className="font-mono font-semibold">{commandForm.pin}</span>
                  </p>
                )}

                {unsyncedEmployees.length > 0 && (
                  <div className="pt-2 border-t mt-3">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => handleSyncAllUnsynced()}
                      disabled={unsyncedLoading || syncProgress.running}
                      data-testid="sync-all-unsynced-btn"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      {syncProgress.running
                        ? `Synchronisation ${syncProgress.completed}/${syncProgress.total}...`
                        : `Synchroniser tous les employés non synchronisés (${unsyncedEmployees.length})`}
                    </Button>

                    {syncProgress.running && (
                      <div className="mt-3 space-y-2" data-testid="sync-progress-panel">
                        <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-600 transition-all duration-300"
                            style={{
                              width: `${syncProgress.total > 0
                                ? Math.round((syncProgress.completed / syncProgress.total) * 100)
                                : 0}%`
                            }}
                            data-testid="sync-progress-bar"
                          />
                        </div>
                        <div className="flex justify-between text-xs text-slate-600">
                          <span>
                            {syncProgress.current
                              ? `En cours: ${syncProgress.current}`
                              : 'Initialisation...'}
                          </span>
                          <span className="font-semibold">
                            {syncProgress.total > 0
                              ? Math.round((syncProgress.completed / syncProgress.total) * 100)
                              : 0}%
                          </span>
                        </div>
                      </div>
                    )}

                    {!syncProgress.running && (
                      <p className="text-xs text-slate-500 mt-2 text-center">
                        Envoie en une seule fois {unsyncedEmployees.length} employé(s) à cette pointeuse
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {commandForm.command_type === 'clear_log' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  Attention : Cette action effacera tous les logs de pointage sur l'appareil.
                </p>
              </div>
            )}
            
            {commandForm.command_type === 'reboot' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  L'appareil sera temporairement hors ligne pendant le redémarrage.
                </p>
              </div>
            )}
            
            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setCommandModalOpen(false)} className="flex-1">
                Annuler
              </Button>
              <Button type="submit" className="flex-1">
                <Terminal className="w-4 h-4 mr-2" />
                Envoyer
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Sync Results Dialog (shows failed employees after bulk-sync) */}
      <Dialog open={showSyncResults} onOpenChange={setShowSyncResults}>
        <DialogContent className="max-w-lg" data-testid="sync-results-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              Récapitulatif de synchronisation
            </DialogTitle>
            <DialogDescription>
              {syncProgress.total - syncProgress.failed.length} réussi(s) sur {syncProgress.total}.
              {syncProgress.failed.length > 0 && ` ${syncProgress.failed.length} employé(s) n'ont pas pu être synchronisés.`}
            </DialogDescription>
          </DialogHeader>

          {syncProgress.failed.length > 0 && (
            <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="sync-failed-list">
              {syncProgress.failed.map((f) => (
                <div
                  key={f.employee_id}
                  className="flex items-start justify-between gap-3 border border-red-200 bg-red-50 rounded-lg p-3"
                  data-testid={`sync-failed-${f.employee_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-red-900">{f.full_name}</p>
                    <p className="text-xs text-red-700 mt-0.5">PIN {f.pin}</p>
                    <p className="text-xs text-red-600 mt-1 break-words">{f.error}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowSyncResults(false);
                setSyncProgress({ running: false, total: 0, completed: 0, current: null, failed: [] });
                setCommandModalOpen(false);
              }}
              className="flex-1"
              data-testid="sync-results-close-btn"
            >
              Fermer
            </Button>
            {syncProgress.failed.length > 0 && (
              <Button
                type="button"
                onClick={handleRetryFailed}
                className="flex-1"
                data-testid="sync-retry-failed-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Relancer les échecs ({syncProgress.failed.length})
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Timezone Editor Dialog (super_admin) */}
      <Dialog open={timezoneModalOpen} onOpenChange={setTimezoneModalOpen}>
        <DialogContent className="max-w-md" data-testid="timezone-dialog">
          <DialogHeader>
            <DialogTitle>Fuseau horaire de la pointeuse</DialogTitle>
            <DialogDescription>
              {selectedDevice && `${selectedDevice.device_name || ''} (${selectedDevice.serial_number || selectedDevice.device_id})`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveTimezone} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="timezone-select">Fuseau IANA *</Label>
              <Select value={timezoneDraft} onValueChange={setTimezoneDraft}>
                <SelectTrigger id="timezone-select" data-testid="timezone-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-96">
                  {[
                    { tz: 'Africa/Kinshasa',    label: 'Africa/Kinshasa (UTC+1)' },
                    { tz: 'Africa/Lubumbashi',  label: 'Africa/Lubumbashi (UTC+2)' },
                    { tz: 'Africa/Abidjan',     label: 'Africa/Abidjan (UTC+0)' },
                    { tz: 'Africa/Dakar',       label: 'Africa/Dakar (UTC+0)' },
                    { tz: 'Africa/Casablanca',  label: 'Africa/Casablanca (UTC+1)' },
                    { tz: 'Africa/Algiers',     label: 'Africa/Algiers (UTC+1)' },
                    { tz: 'Africa/Tunis',       label: 'Africa/Tunis (UTC+1)' },
                    { tz: 'Africa/Cairo',       label: 'Africa/Cairo (UTC+2)' },
                    { tz: 'Africa/Nairobi',     label: 'Africa/Nairobi (UTC+3)' },
                    { tz: 'Africa/Johannesburg',label: 'Africa/Johannesburg (UTC+2)' },
                    { tz: 'Europe/Paris',       label: 'Europe/Paris (UTC+1/+2)' },
                    { tz: 'Europe/London',      label: 'Europe/London (UTC+0/+1)' },
                    { tz: 'Europe/Brussels',    label: 'Europe/Brussels (UTC+1/+2)' },
                    { tz: 'America/New_York',   label: 'America/New_York (UTC-5/-4)' },
                    { tz: 'UTC',                label: 'UTC (UTC+0)' },
                  ].map(opt => (
                    <SelectItem key={opt.tz} value={opt.tz} data-testid={`tz-option-${opt.tz}`}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                L'heure envoyée par la pointeuse sera interprétée comme étant dans ce fuseau.
              </p>
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setTimezoneModalOpen(false)} className="flex-1">
                Annuler
              </Button>
              <Button type="submit" className="flex-1" data-testid="save-timezone-btn">
                <CheckCircle className="w-4 h-4 mr-2" />
                Enregistrer
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Device Dialog */}
      <Dialog open={editDeviceModalOpen} onOpenChange={setEditDeviceModalOpen}>
        <DialogContent className="max-w-lg" data-testid="edit-device-dialog">
          <DialogHeader>
            <DialogTitle>Modifier la pointeuse</DialogTitle>
            <DialogDescription>
              {selectedDevice && (
                <>N° série <span className="font-mono">{selectedDevice.serial_number || selectedDevice.device_id}</span></>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveEditDevice} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit_device_name">Nom de l&apos;appareil *</Label>
              <Input
                id="edit_device_name"
                value={editDeviceForm.device_name}
                onChange={(e) => setEditDeviceForm({ ...editDeviceForm, device_name: e.target.value })}
                placeholder="Pointeuse Entrée Principale"
                required
                data-testid="edit-device-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_location">Emplacement *</Label>
              <Input
                id="edit_location"
                value={editDeviceForm.location}
                onChange={(e) => setEditDeviceForm({ ...editDeviceForm, location: e.target.value })}
                placeholder="Hall d'entrée, Bâtiment A"
                required
                data-testid="edit-device-location-input"
              />
            </div>

            {companies.length > 1 && (
              <div className="space-y-2">
                <Label>Entreprises additionnelles (multi-tenant)</Label>
                <p className="text-xs text-slate-500">
                  Cochez d&apos;autres entreprises dont les employés peuvent pointer sur cette pointeuse.
                  L&apos;entreprise principale ({companies.find(c => c.id === selectedDevice?.company_id)?.name || '—'}) est toujours autorisée.
                </p>
                <div
                  className="border rounded-md p-2 max-h-40 overflow-y-auto space-y-1 bg-slate-50"
                  data-testid="edit-device-allowed-companies"
                >
                  {companies.filter(c => c.id !== selectedDevice?.company_id).map((c) => {
                    const checked = (editDeviceForm.allowed_company_ids || []).includes(c.id);
                    return (
                      <label
                        key={c.id}
                        className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const current = editDeviceForm.allowed_company_ids || [];
                            setEditDeviceForm({
                              ...editDeviceForm,
                              allowed_company_ids: e.target.checked
                                ? [...current, c.id]
                                : current.filter((x) => x !== c.id),
                            });
                          }}
                          data-testid={`edit-device-allow-${c.id}`}
                        />
                        <span>{c.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditDeviceModalOpen(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button type="submit" className="flex-1" data-testid="save-edit-device-btn">
                <CheckCircle className="w-4 h-4 mr-2" />
                Enregistrer
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Modal Alertes Hors-Ligne ── */}
      <Dialog open={alertModalOpen} onOpenChange={setAlertModalOpen}>
        <DialogContent className="max-w-lg" data-testid="alert-recipients-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-orange-500" />
              Alertes hors-ligne — {selectedDevice?.device_name}
            </DialogTitle>
            <DialogDescription>
              Recevez un email dès que cette pointeuse cesse d'être joignable.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Seuil */}
            <div>
              <Label className="text-sm font-medium text-slate-700">
                Délai avant alerte (minutes)
              </Label>
              <p className="text-xs text-slate-500 mb-2">
                L'alerte est envoyée si la pointeuse n'a pas donné signe de vie depuis ce délai.
              </p>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min={1}
                  max={1440}
                  value={alertForm.threshold}
                  onChange={e => setAlertForm(prev => ({ ...prev, threshold: parseInt(e.target.value) || 15 }))}
                  className="w-24 h-10 px-3 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <span className="text-sm text-slate-500">minutes</span>
                <span className="text-xs text-slate-400">(recommandé : 15)</span>
              </div>
            </div>

            {/* Destinataires */}
            <div>
              <Label className="text-sm font-medium text-slate-700">
                Destinataires des alertes
              </Label>
              <p className="text-xs text-slate-500 mb-3">
                Ces adresses recevront un email d'alerte et un email de rétablissement.
              </p>

              {/* Liste des destinataires */}
              <div className="space-y-2 mb-3 max-h-40 overflow-y-auto">
                {alertForm.recipients.length === 0 ? (
                  <p className="text-sm text-slate-400 italic py-2">Aucun destinataire configuré</p>
                ) : (
                  alertForm.recipients.map(email => (
                    <div
                      key={email}
                      className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2"
                    >
                      <div className="flex items-center gap-2">
                        <Mail className="w-3.5 h-3.5 text-slate-400" />
                        <span className="text-sm text-slate-700">{email}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAlertEmail(email)}
                        className="text-slate-400 hover:text-red-500 transition-colors"
                        title="Retirer"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>

              {/* Ajout d'un email */}
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="nom@exemple.com"
                  value={alertForm.newEmail}
                  onChange={e => setAlertForm(prev => ({ ...prev, newEmail: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addAlertEmail(); } }}
                  className="flex-1"
                  data-testid="alert-email-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={addAlertEmail}
                  data-testid="add-alert-email-btn"
                >
                  <PlusCircle className="w-4 h-4 text-green-600" />
                </Button>
              </div>
              <p className="text-xs text-slate-400 mt-1">Appuyez sur Entrée ou cliquez + pour ajouter</p>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setAlertModalOpen(false)}
            >
              Annuler
            </Button>
            <Button
              className="flex-1"
              onClick={handleSaveAlertRecipients}
              disabled={alertSaving}
              data-testid="save-alert-recipients-btn"
            >
              {alertSaving ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Enregistrer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
